// Library Methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FigureRow, Figures } from "../../shared/FigureRow";
import SummaryCard, { NothingToSummarise } from "../../shared/SummaryCard";
import {
  DistanceKindType,
  DistanceTag,
  FigureColumn,
  FigureColumns,
} from "../../shared/FigureColumn";
import {
  HorizontalVergenceFigure,
  VerticalVergenceFigure,
} from "../../shared/figures/VergenceHeadFigure";
import BreakBar from "./BreakBar";
import FacilityChart, { FacilityHalfType } from "./FacilityChart";
import NearPointChart from "./NearPointChart";

// Types
import {
  ExamSectionReportType,
  FusionalAmplitudesSummaryType,
  FusionalRangeSummaryType,
} from "src/services/exams/types";

// Utils
import { num } from "../../shared/formatters";

type SummaryType = NonNullable<
  ExamSectionReportType["postProcessing"]["summary"]
>;
type ModulesType = NonNullable<
  ExamSectionReportType["postProcessing"]["modules"]
>;

type SummarySectionType = {
  summary: SummaryType | null | undefined;
  modules: ModulesType | null | undefined;
};

const DISTANCES: Array<DistanceKindType> = ["far", "near"];

const Sentence = ({ children }: { children: ReactNode }) => (
  <Typography fontSize="18px" fontWeight={700} sx={{ lineHeight: 1.35 }}>
    {children}
  </Typography>
);

const RunLine = ({ children }: { children: ReactNode }) => (
  <Typography fontSize="12px" color="#5B6D7A" sx={{ lineHeight: 1.5 }}>
    {children}
  </Typography>
);

const Say = ({ children }: { children: ReactNode }) => (
  <Typography fontSize="12px" color="#5B6D7A">
    {children}
  </Typography>
);

const CardBody = ({ children }: { children: ReactNode }) => (
  <Box sx={{ pb: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
    {children}
  </Box>
);

/**
 * One direction's range: what the prism did to the eyes, then how far it got before the
 * picture split.
 */
const RangeColumn = ({
  title,
  kind,
  caption,
  range,
  domain,
}: {
  title: string;
  kind: "in" | "out";
  caption: string;
  range: FusionalRangeSummaryType;
  domain: number;
}) => {
  const { t } = useTranslation();

  return (
    <FigureColumn title={title}>
      <Box sx={{ maxWidth: "140px", width: "100%", mx: "auto" }}>
        <HorizontalVergenceFigure kind={kind} caption={caption} />
      </Box>
      <BreakBar
        breakPrismDioptres={range.breakPrismDioptres}
        recoveryPrismDioptres={range.recoveryPrismDioptres}
        domain={domain}
        isLowerBound={range.isLowerBound}
      />
      {/* "(lower bound)" was a parenthesis that reversed the meaning of the number in front
          of it. The sentence says which of the two things happened. */}
      <Say>
        {range.isLowerBound
          ? t(
              "Never split: {{value}}Δ is as far as this test goes, so the real limit is further out",
              { value: num(range.breakPrismDioptres, 0) }
            )
          : t(
              "Doubled at {{breakValue}}Δ, single again at {{recoveryValue}}Δ",
              {
                breakValue: num(range.breakPrismDioptres, 0),
                recoveryValue: num(range.recoveryPrismDioptres, 0),
              }
            )}
      </Say>
    </FigureColumn>
  );
};

/**
 * How far the eyes can be pushed, at one distance.
 */
const AmplitudesCard = ({
  distance,
  data,
}: {
  distance: DistanceKindType;
  data: FusionalAmplitudesSummaryType | null | undefined;
}) => {
  const { t } = useTranslation();

  // Both directions share one scale, so the asymmetry between them can be read off the bars.
  const domain =
    Math.max(
      data?.convergence?.breakPrismDioptres ?? 0,
      data?.divergence?.breakPrismDioptres ?? 0,
      1
    ) * 1.2;

  return (
    <SummaryCard
      title={
        <>
          <Box component="span">{t("How far the eyes can be pushed")}</Box>
          <DistanceTag distance={distance} />
        </>
      }
      cautions={null}
    >
      <CardBody>
        {!data ? (
          <NothingToSummarise />
        ) : (
          <>
            <FigureColumns>
              {data.convergence && (
                <RangeColumn
                  title={t("Pulling the eyes in (convergence)")}
                  kind="in"
                  caption={t("eyes pulled in")}
                  range={data.convergence}
                  domain={domain}
                />
              )}
              {data.divergence && (
                <RangeColumn
                  title={t("Letting the eyes out (divergence)")}
                  kind="out"
                  caption={t("eyes pulled out")}
                  range={data.divergence}
                  domain={domain}
                />
              )}
              {data.verticalTotal && (
                <FigureColumn title={t("Up and down")}>
                  <Box sx={{ maxWidth: "140px", width: "100%", mx: "auto" }}>
                    <VerticalVergenceFigure caption={t("one up, one down")} />
                  </Box>
                  <Typography
                    fontSize="18px"
                    fontWeight={700}
                    sx={{ lineHeight: 1.35 }}
                  >
                    {num(data.verticalTotal.prismDioptres, 1, "Δ")}
                  </Typography>
                  {/* "Vertical Total" meant up and down added together, which nothing said. */}
                  <Say>
                    {t(
                      "How far one eye can be pushed above the other before the picture splits, both directions added together"
                    )}
                  </Say>
                </FigureColumn>
              )}
            </FigureColumns>

            <RunLine>
              {t(
                "The bar runs from no prism out to the point the picture split in two. The upright tick is where it came back together as the prism was wound back off."
              )}
            </RunLine>
          </>
        )}
      </CardBody>
    </SummaryCard>
  );
};

/**
 * Whether the two eyes' pictures went together at all, which is the precondition for every
 * range beneath it.
 */
const FusionCard = ({
  data,
}: {
  data: FusionalAmplitudesSummaryType | null | undefined;
}) => {
  const { t } = useTranslation();

  if (!data) return null;

  return (
    <SummaryCard title={t("Holding the two pictures together")} cautions={null}>
      <CardBody>
        <Sentence>
          {data.fusionPresent
            ? t(
                "The patient can put the two eyes' pictures together and keep them together."
              )
            : t("The patient could not put the two eyes' pictures together.")}
        </Sentence>
        <Figures>
          <FigureRow
            label={t("Fusion")}
            value={data.fusionPresent ? t("Yes") : t("No")}
            sub={t("The two pictures were joined into one")}
          />
          <FigureRow
            label={t("Fusion Quality")}
            value={data.fusionQuality ? t(data.fusionQuality) : t("N/A")}
            sub={t(
              "Which part of the retina the two pictures were joined with"
            )}
          />
          <FigureRow
            label={t("Eye being suppressed")}
            value={
              data.suppressedEye && data.suppressedEye !== "None"
                ? t(data.suppressedEye)
                : t("None")
            }
            sub={t("Whether either eye was switched off during the run")}
          />
        </Figures>
      </CardBody>
    </SummaryCard>
  );
};

/**
 * How fast the eyes recover: every flip of the prism, charted.
 *
 * The chart replaces `Facility 7.0 cycles/min` and `Limiting Direction BaseOut`. A time the
 * patient took reads better than a rate per minute, and the two median times and the per-flip
 * list are all already on the payload.
 */
const FacilityCard = ({
  facility,
  halves,
}: {
  facility:
    | {
        baseIn: { medianSecondsToClear: number };
        baseOut: { medianSecondsToClear: number };
        limitingDirection: string | null;
      }
    | null
    | undefined;
  halves: Array<FacilityHalfType>;
}) => {
  const { t } = useTranslation();

  if (!facility) return null;

  const slower =
    facility.limitingDirection === null || facility.limitingDirection === "None"
      ? null
      : /baseout/i.test(facility.limitingDirection)
        ? { value: "Converging", note: "Base out" }
        : { value: "Relaxing", note: "Base in" };

  return (
    <SummaryCard title={t("How fast the eyes recover")} cautions={null}>
      <CardBody>
        {halves.length > 0 && (
          <>
            <FacilityChart
              halves={halves}
              medianBaseOutSeconds={
                facility.baseOut?.medianSecondsToClear ?? null
              }
              medianBaseInSeconds={
                facility.baseIn?.medianSecondsToClear ?? null
              }
            />
            <Say>
              {t(
                "Each bar is one flip of the prism, in the order it was shown, and its height is how long the patient took to get back to one clear picture. Base out makes the eyes converge and base in lets them relax. A dashed outline with a cross is a flip the patient never cleared. The dashed line across each half is the median — half the flips were quicker than it and half slower."
              )}
            </Say>
          </>
        )}

        {slower && (
          <Figures>
            <FigureRow
              label={t("Slower direction")}
              value={t(slower.value)}
              sub={t(
                "{{prism}}. Where the delay is, which is what changes the exercises given",
                { prism: t(slower.note) }
              )}
            />
          </Figures>
        )}

        <RunLine>
          {t(
            "The test flips a prism back and forth and times how long the patient takes to report one clear picture each time."
          )}
        </RunLine>
      </CardBody>
    </SummaryCard>
  );
};

/**
 * Everything the vergence run measured apart from the facility: the near point and the AC/A.
 */
const VergenceFunctionCards = ({
  data,
}: {
  data: SummaryType["VergenceFunction"];
}) => {
  const { t } = useTranslation();

  if (!data) return null;

  return (
    <>
      {data.nearPoint && (
        <SummaryCard
          title={t("Following a target towards the nose")}
          cautions={null}
        >
          <CardBody>
            <NearPointChart nearPoint={data.nearPoint} />
            <Figures>
              <FigureRow
                label={t("Reached the nose")}
                value={data.nearPoint.reachedTheNose ? t("Yes") : t("No")}
                sub={t("Whether the target stayed single all the way in")}
              />
              {/* "Recedes" names the direction the number moves, not what it means. */}
              <FigureRow
                label={t("Gets worse with repetition")}
                value={data.nearPoint.recedes ? t("Yes") : t("No")}
                sub={t("Whether the break point moved outwards over the tries")}
              />
            </Figures>
          </CardBody>
        </SummaryCard>
      )}

      {data.aca && (
        <SummaryCard
          title={t("How much the eyes turn in when focusing up close")}
          cautions={null}
        >
          <CardBody>
            <Figures>
              <FigureRow
                label={t("AC/A Ratio")}
                value={num(data.aca.ratio, 1)}
                sub={t(
                  "Prism dioptres the eyes turn in for each dioptre of focusing"
                )}
              />
              {/* `VergenceDrivenOnly` is an enum name, and it is the only value a
                  fixed-focus headset can produce. */}
              <FigureRow
                label={t("How it was worked out")}
                value={t("From the two distances")}
                sub={t(
                  "Measured by comparing the two viewing distances, not by changing the focus"
                )}
              />
            </Figures>
            <RunLine>
              {t(
                "A headset cannot change how hard the eyes focus, so this figure comes from comparing the alignment at the two distances. It is a usable number, but it is not the one a clinic would get from a phoropter."
              )}
            </RunLine>
          </CardBody>
        </SummaryCard>
      )}
    </>
  );
};

/**
 * The section as a clinic user reads it.
 *
 * No cautions and no incomplete-modules list: what matters is said on the field it is about.
 *
 * No deviation fields either. Distance (Horizontal), Distance (Vertical), Near (Horizontal)
 * and Near (Vertical) are Deviation & Correspondence's findings and were printing in both
 * places. They are still measured -- the vergence run needs a starting alignment and the AC/A
 * is the difference between the two -- they just stop being reported here.
 */
const SummarySection = ({ summary, modules }: SummarySectionType) => {
  if (!summary) return null;

  const facilityModule = modules?.VergenceFunction?.facility;

  // Every flip, from the verbose module block. The summary carries only the aggregates.
  const halves: Array<FacilityHalfType> = (facilityModule?.halves ?? []).map(
    (half) => ({
      direction: half.direction,
      secondsToClear: half.secondsToClear,
    })
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <FusionCard
        data={
          summary.FusionalAmplitudes?.far ?? summary.FusionalAmplitudes?.near
        }
      />

      {DISTANCES.map((distance) => (
        <AmplitudesCard
          key={distance}
          distance={distance}
          data={summary.FusionalAmplitudes?.[distance]}
        />
      ))}

      <VergenceFunctionCards data={summary.VergenceFunction} />

      <FacilityCard
        facility={
          facilityModule
            ? {
                baseIn: facilityModule.baseIn,
                baseOut: facilityModule.baseOut,
                limitingDirection:
                  summary.VergenceFunction?.facility?.limitingDirection ??
                  facilityModule.limitingDirection,
              }
            : null
        }
        halves={halves}
      />
    </Box>
  );
};

export default SummarySection;
